import React, { useState } from 'react';
import { uid } from '../db';
import { Btn, Card, CardHead, Field, Grid, Input, Modal, ModalFooter, NoData, Select, Span, toast } from '../ui.jsx';

function Classes({ db, save }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [editId, setEditId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [actionMenu, setActionMenu] = useState(null); // student id with open menu
  const [perfModal, setPerfModal] = useState(null);   // { student }
  const [perfForm, setPerfForm] = useState({});
  const sf = k => v => setForm(f => ({ ...f, [k]: v }));

  const openAdd = () => { setForm({}); setEditId(null); setOpen(true); };
  const openEdit = (c) => { setForm({ ...c }); setEditId(c.id); setOpen(true); };

  const DEFAULT_CLASSES = [
    { name: 'Playgroup', subs: 'Hindi, English, Mathematics, Rhymes', coSubs: 'General Knowledge, Art Education, Yoga / P.T.' },
    { name: 'Nursery', subs: 'Hindi, English, Mathematics, Rhymes', coSubs: 'General Knowledge, Art Education, Yoga / P.T.' },
    { name: 'L.K.G.', subs: 'Hindi, English, Mathematics, Rhymes', coSubs: 'General Knowledge, Art Education, Yoga / P.T.' },
    { name: 'U.K.G.', subs: 'Hindi, English, Mathematics, Rhymes', coSubs: 'General Knowledge, Art Education, Yoga / P.T.' },
    { name: '1st', subs: 'English, Hindi, Mathematics, Science, S.St., Computer', coSubs: 'General Knowledge, Art Education, Yoga / P.T.' },
    { name: '2nd', subs: 'English, Hindi, Mathematics, Science, S.St., Computer', coSubs: 'General Knowledge, Art Education, Yoga / P.T.' },
    { name: '3rd', subs: 'English, Hindi, Mathematics, Science, S.St., Computer', coSubs: 'General Knowledge, Art Education, Yoga / P.T.' },
    { name: '4th', subs: 'English, Hindi, Mathematics, Science, S.St., Computer', coSubs: 'General Knowledge, Art Education, Yoga / P.T.' },
    { name: '5th', subs: 'English, Hindi, Mathematics, Science, S.St., Computer', coSubs: 'General Knowledge, Art Education, Yoga / P.T.' },
    { name: '6th', subs: 'English, Hindi, Mathematics, Science, S.St., Computer', coSubs: 'General Knowledge, Art Education, Yoga / P.T.' },
    { name: '7th', subs: 'English, Hindi, Mathematics, Science, S.St., Computer', coSubs: 'General Knowledge, Art Education, Yoga / P.T.' },
    { name: '8th', subs: 'English, Hindi, Mathematics, Science, S.St., Computer', coSubs: 'General Knowledge, Art Education, Yoga / P.T.' },
  ];

  const DEFAULT_CO_SUBS = 'General Knowledge, Art Education, Yoga / P.T.';

  const addDefaultClasses = () => {
    const existing = new Set(db.classes.map(c => c.name));
    const toAdd = DEFAULT_CLASSES.filter(c => !existing.has(c.name))
      .map(c => ({ id: 'C' + uid(), ...c }));
    // Also update existing classes that are missing coSubs
    const updated = db.classes.map(c => c.coSubs ? c : { ...c, coSubs: DEFAULT_CO_SUBS });
    const changed = updated.filter((c, i) => !db.classes[i].coSubs).length;
    save({ ...db, classes: [...updated, ...toAdd] });
    const msg = [];
    if (toAdd.length) msg.push(`Added ${toAdd.length} class${toAdd.length > 1 ? 'es' : ''}`);
    if (changed) msg.push(`Updated ${changed} class${changed > 1 ? 'es' : ''} with co-scholastic subjects`);
    toast(msg.length ? msg.join(', ') : 'All classes already up to date');
  };

  // Auto-fill subjects when class name is typed
  const autoSubjects = (name) => {
    const lower = name.toLowerCase().trim();
    const earlyClasses = ['playgroup', 'nursery', 'l.k.g.', 'lkg', 'u.k.g.', 'ukg'];
    if (earlyClasses.some(n => lower === n || lower.replace(/\./g, '') === n.replace(/\./g, '')))
      return 'Hindi, English, Mathematics, Rhymes';
    const match = lower.match(/^(\d+)(st|nd|rd|th)?$/);
    if (match && parseInt(match[1]) >= 1 && parseInt(match[1]) <= 8)
      return 'English, Hindi, Mathematics, Science, S.St., Computer';
    return null;
  };

  const saveCls = () => {
    if (!form.name?.trim()) { toast('Name required', 'err'); return; }
    const withDefaults = { ...form, coSubs: form.coSubs || DEFAULT_CO_SUBS };
    if (editId) {
      const updated = { ...withDefaults, id: editId };
      save({ ...db, classes: db.classes.map(c => c.id === editId ? updated : c) });
      setSelected(updated);
      toast('Class updated');
    } else {
      save({ ...db, classes: [...db.classes, { id: 'C' + uid(), ...withDefaults }] });
      toast('Class added');
    }
    setForm({}); setEditId(null); setOpen(false);
  };
  const delCls = (id) => {
    if (!window.confirm('Delete this class?')) return;
    save({ ...db, classes: db.classes.filter(c => c.id !== id) });
    if (selected?.id === id) setSelected(null);
    toast('Class removed', 'err');
  };

  const totalStudents = db.students.length;
  const totalTeachers = db.teachers.length;

  // Auto-select first class
  const activeClass = selected || db.classes[0] || null;
  const activeStudents = activeClass ? db.students.filter(s => s.cls === activeClass.name) : [];
  const activeTeacher = activeClass ? db.teachers.find(t => (t.cls || '').split(',').map(x => x.trim()).includes(activeClass.name)) : null;

  return (
    <div>
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
        <div>
          <h2 className="text-4xl font-extrabold text-primary tracking-tight font-headline mb-2">Class Management</h2>
          <p className="text-on-surface-variant font-medium">Coordinate class rosters, faculty assignments, and administrative settings.</p>
        </div>
        <div className="flex gap-3">
          <button onClick={addDefaultClasses}
            className="flex items-center gap-2 bg-surface-container-lowest text-primary px-5 py-3 rounded-xl font-bold shadow-sm border border-outline-variant hover:-translate-y-0.5 transition-all cursor-pointer text-sm">
            <span className="material-symbols-outlined text-lg">auto_awesome</span>
            Add Default Classes
          </button>
          <button onClick={openAdd}
            className="flex items-center gap-2 bg-primary text-on-primary px-6 py-3 rounded-xl font-bold shadow-xl shadow-primary/10 hover:-translate-y-0.5 transition-all border-0 cursor-pointer font-sans text-sm">
            <span className="material-symbols-outlined text-lg">add</span>
            Create New Class
          </button>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-5 mb-8">
        <div className="bg-surface-container-lowest p-5 rounded-xl border-l-4 border-primary">
          <p className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant mb-1">Total Classes</p>
          <p className="text-3xl font-extrabold text-primary font-headline">{db.classes.length}</p>
        </div>
        <div className="bg-surface-container-lowest p-5 rounded-xl border-l-4 border-secondary">
          <p className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant mb-1">Total Students</p>
          <p className="text-3xl font-extrabold text-primary font-headline">{totalStudents}</p>
        </div>
        <div className="bg-surface-container-lowest p-5 rounded-xl border-l-4 border-on-tertiary-container">
          <p className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant mb-1">Total Teachers</p>
          <p className="text-3xl font-extrabold text-primary font-headline">{totalTeachers}</p>
        </div>
      </div>

      {!db.classes.length ? (
        <div className="bg-surface-container-lowest rounded-2xl p-16 text-center">
          <span className="material-symbols-outlined text-5xl text-outline mb-4 block">class</span>
          <p className="text-lg font-bold text-primary font-headline mb-1">No classes yet</p>
          <p className="text-sm text-on-surface-variant mb-6">Create your first class to get started.</p>
          <div className="flex gap-3 justify-center">
            <button onClick={addDefaultClasses}
              className="inline-flex items-center gap-2 bg-secondary-container text-on-secondary-container px-6 py-3 rounded-xl font-bold text-sm shadow-sm border-0 cursor-pointer font-sans">
              <span className="material-symbols-outlined text-lg">auto_awesome</span> Add Default Classes
            </button>
            <button onClick={() => { setForm({}); setOpen(true); }}
              className="inline-flex items-center gap-2 bg-primary text-on-primary px-6 py-3 rounded-xl font-bold text-sm shadow-lg shadow-primary/20 border-0 cursor-pointer font-sans">
              <span className="material-symbols-outlined text-lg">add</span> Create New Class
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Left: Class List */}
          <div className="lg:col-span-4 space-y-4">
            <h3 className="text-[10px] font-bold uppercase tracking-widest text-slate-400 px-1">Active Classes</h3>
            {db.classes.map((c, i) => {
              const sc = db.students.filter(s => s.cls === c.name).length;
              const tea = db.teachers.find(t => t.cls === c.name);
              const isActive = activeClass?.id === c.id;
              return (
                <div key={c.id} onClick={() => setSelected(c)}
                  className={`group relative p-6 rounded-2xl cursor-pointer transition-all ${isActive
                    ? 'bg-surface-container-lowest shadow-sm border-l-4 border-primary'
                    : 'bg-surface-container-low hover:bg-surface-container-lowest border-l-4 border-transparent'}`}>
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h4 className={`text-2xl font-black font-headline transition-colors ${isActive ? 'text-primary' : 'text-slate-400 group-hover:text-primary'}`}>{c.name}</h4>
                      <p className="text-sm text-on-surface-variant">{c.room ? `Room ${c.room}` : 'No room assigned'}</p>
                    </div>
                    <span className={`text-[10px] font-bold px-2 py-1 rounded-full uppercase tracking-tighter ${isActive ? 'bg-secondary-container text-on-secondary-container' : 'bg-surface-container-high text-slate-500'}`}>
                      {c.stream || 'General'}
                    </span>
                  </div>
                  <div className={`flex items-center gap-3 transition-opacity ${isActive ? 'opacity-100' : 'opacity-60 group-hover:opacity-100'}`}>
                    <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-xs font-bold text-on-primary flex-shrink-0">
                      {tea ? (tea.fn[0] || '') + (tea.ln[0] || '') : '?'}
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Class Teacher</p>
                      <p className="text-sm font-bold text-primary">{tea ? tea.fn + ' ' + tea.ln : 'Not assigned'}</p>
                    </div>
                    <div className="ml-auto text-right">
                      <p className="text-xs text-slate-500">Students</p>
                      <p className="text-sm font-bold text-primary">{sc}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Right: Detail Panel */}
          {activeClass && (
            <div className="lg:col-span-8 bg-surface-container-lowest rounded-3xl overflow-hidden shadow-sm flex flex-col min-h-[500px]">
              {/* Detail Header */}
              <div className="p-8 bg-primary text-on-primary rounded-t-3xl">
                <div className="flex justify-between items-start gap-4">
                  <div>
                    <span className="text-blue-300 text-xs font-bold uppercase tracking-[0.2em] mb-2 block">Viewing Details</span>
                    <h3 className="text-5xl font-black font-headline leading-tight">{activeClass.name}</h3>
                    <div className="flex items-center gap-6 mt-4">
                      <div className="flex items-center gap-2">
                        <span className="material-symbols-outlined text-blue-300 text-sm">groups</span>
                        <span className="text-sm font-medium">{activeStudents.length} Students</span>
                      </div>
                      {activeClass.room && (
                        <div className="flex items-center gap-2">
                          <span className="material-symbols-outlined text-blue-300 text-sm">meeting_room</span>
                          <span className="text-sm font-medium">Room {activeClass.room}</span>
                        </div>
                      )}
                      {activeClass.stream && (
                        <div className="flex items-center gap-2">
                          <span className="material-symbols-outlined text-blue-300 text-sm">school</span>
                          <span className="text-sm font-medium">{activeClass.stream}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={(e) => { e.stopPropagation(); openEdit(activeClass); }}
                      className="p-3 bg-white/10 hover:bg-white/20 rounded-xl transition-colors border-0 cursor-pointer text-on-primary">
                      <span className="material-symbols-outlined">edit</span>
                    </button>
                    <button onClick={() => delCls(activeClass.id)}
                      className="p-3 bg-white/10 hover:bg-error/30 rounded-xl transition-colors border-0 cursor-pointer text-on-primary">
                      <span className="material-symbols-outlined">delete</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Subjects */}
              {activeClass.subs && (
                <div className="px-8 pt-6 pb-4 border-b border-surface-container-high bg-surface-container-lowest">
                  {activeClass.subs && (
                    <>
                      <p className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant mb-3">Theory Subjects</p>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {activeClass.subs.split(',').map(s => s.trim()).filter(Boolean).map(sub => (
                          <span key={sub} className="bg-primary/8 text-primary text-xs font-bold px-3 py-1 rounded-full border border-primary/15">{sub}</span>
                        ))}
                      </div>
                    </>
                  )}
                  <>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant mb-3">Co-Scholastic Subjects</p>
                    <div className="flex flex-wrap gap-2">
                      {(activeClass.coSubs || DEFAULT_CO_SUBS).split(',').map(s => s.trim()).filter(Boolean).map(sub => (
                        <span key={sub} className="bg-emerald-500/10 text-emerald-700 text-xs font-bold px-3 py-1 rounded-full border border-emerald-500/20">{sub}</span>
                      ))}
                    </div>
                  </>
                </div>
              )}

              {/* Student Roster */}
              <div className="flex-1 overflow-y-auto p-8 bg-surface" onClick={() => setActionMenu(null)}>
                <p className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant mb-4">Student Roster</p>
                {activeStudents.length === 0 ? (
                  <div className="text-center py-12 text-on-surface-variant text-sm">No students enrolled in this class.</div>
                ) : (
                  <table className="w-full text-left">
                    <thead>
                      <tr className="text-on-surface-variant text-[10px] uppercase tracking-[0.1em] border-b border-surface-container">
                        <th className="py-4 font-semibold px-2">Roll No.</th>
                        <th className="py-4 font-semibold px-4">Student Name</th>
                        <th className="py-4 font-semibold px-4">Attendance</th>
                        <th className="py-4 font-semibold px-4">Performance</th>
                        <th className="py-4 font-semibold text-right px-2">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-surface-container-low">
                      {activeStudents.map((s, idx) => {
                        // Real-time attendance from db.att
                        let present = 0, total = 0;
                        Object.values(db.att).forEach(day => {
                          if (day[s.id] !== undefined) {
                            total++;
                            if (day[s.id] === 'P' || day[s.id] === 'L') present++;
                          }
                        });
                        const attPct = total > 0 ? Math.round(present / total * 100) : null;
                        const attBarColor = attPct === null ? 'bg-surface-container' : attPct >= 90 ? 'bg-secondary' : attPct >= 75 ? 'bg-on-tertiary-container' : 'bg-error';
                        const attTextColor = attPct === null ? 'text-outline' : attPct >= 90 ? 'text-secondary' : attPct >= 75 ? 'text-on-tertiary-fixed-variant' : 'text-error';

                        // Performance: use stored override or derive from attendance
                        const perf = s.perf || (attPct === null ? null : attPct >= 90 ? 'Exceeds' : attPct >= 75 ? 'Meeting' : 'At Risk');
                        const perfStyle = perf === 'Exceeds' ? 'bg-secondary-fixed text-on-secondary-fixed'
                          : perf === 'Meeting' ? 'bg-tertiary-fixed text-on-tertiary-fixed'
                            : perf === 'At Risk' ? 'bg-error-container text-on-error-container'
                              : 'bg-surface-container text-outline';

                        return (
                          <tr key={s.id} className="group hover:bg-surface-container-low transition-colors">
                            <td className="py-5 font-headline font-bold text-primary px-2 text-sm">{s.roll || (idx + 1)}</td>
                            <td className="py-5 px-4">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center font-bold text-primary text-xs flex-shrink-0">
                                  {(s.fn[0] || '') + (s.ln[0] || '')}
                                </div>
                                <span className="font-semibold text-primary text-sm">{s.fn} {s.ln}</span>
                              </div>
                            </td>
                            <td className="py-5 px-4">
                              <div className="flex items-center gap-2">
                                <div className="w-24 h-1.5 bg-surface-container rounded-full overflow-hidden">
                                  <div className={`h-full rounded-full ${attBarColor}`} style={{ width: attPct !== null ? attPct + '%' : '0%' }}></div>
                                </div>
                                <span className={`text-[10px] font-bold ${attTextColor}`}>{attPct !== null ? attPct + '%' : '—'}</span>
                              </div>
                            </td>
                            <td className="py-5 px-4">
                              {perf ? (
                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase ${perfStyle}`}>{perf}</span>
                              ) : (
                                <span className="text-[10px] text-outline">No data</span>
                              )}
                            </td>
                            <td className="py-5 text-right px-2 relative">
                              <button onClick={(e) => { e.stopPropagation(); setActionMenu(actionMenu === s.id ? null : s.id); }}
                                className="text-slate-400 hover:text-primary border-0 bg-transparent cursor-pointer p-1 rounded transition-colors">
                                <span className="material-symbols-outlined">more_vert</span>
                              </button>
                              {actionMenu === s.id && (
                                <div className="absolute right-2 top-10 z-50 bg-surface-container-lowest rounded-xl shadow-xl border border-surface-container-high w-44 py-1 text-left" onClick={e => e.stopPropagation()}>
                                  <p className="text-[9px] font-bold uppercase tracking-widest text-outline px-4 pt-2 pb-1">Set Performance</p>
                                  {['Exceeds', 'Meeting', 'At Risk'].map(p => (
                                    <button key={p} onClick={() => {
                                      const updated = db.students.map(x => x.id === s.id ? { ...x, perf: p } : x);
                                      save({ ...db, students: updated });
                                      setActionMenu(null);
                                      toast('Performance updated');
                                    }} className={`w-full text-left px-4 py-2 text-xs font-semibold hover:bg-surface-container-low transition-colors border-0 bg-transparent cursor-pointer ${perf === p ? 'text-primary' : 'text-on-surface'}`}>
                                      {p === 'Exceeds' ? '⭐ Exceeds' : p === 'Meeting' ? '✓ Meeting' : '⚠ At Risk'}
                                    </button>
                                  ))}
                                  <div className="border-t border-surface-container-high my-1"></div>
                                  <button onClick={() => {
                                    const updated = db.students.map(x => x.id === s.id ? { ...x, perf: undefined } : x);
                                    save({ ...db, students: updated });
                                    setActionMenu(null);
                                    toast('Reset to auto');
                                  }} className="w-full text-left px-4 py-2 text-xs font-semibold text-outline hover:bg-surface-container-low transition-colors border-0 bg-transparent cursor-pointer">
                                    ↺ Reset to Auto
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      <Modal open={open} onClose={() => { setOpen(false); setEditId(null); }} title={editId ? 'Edit Class' : 'Create New Class'}>
        <Grid>
          <Field label="Class Name *"><Input value={form.name || ''} onChange={v => {
            sf('name')(v);
            if (!editId && !form.subs) {
              const auto = autoSubjects(v);
              if (auto) setForm(f => ({ ...f, name: v, subs: auto }));
            }
          }} placeholder="e.g. Nursery, 1st, 5th" /></Field>
          <Field label="Stream"><Input value={form.stream || ''} onChange={sf('stream')} placeholder="e.g. General, Science" /></Field>
          <Field label="Room"><Input value={form.room || ''} onChange={sf('room')} placeholder="Enter room number" /></Field>
          <Field label="Strength"><Input type="number" value={form.str || ''} onChange={sf('str')} placeholder="Max students in class" /></Field>
          <Span><Field label="Theory Subjects (comma separated)"><Input value={form.subs || ''} onChange={sf('subs')} placeholder="e.g. English, Hindi, Mathematics, Science" /></Field></Span>
          <Span><Field label="Co-Scholastic Subjects (comma separated)"><Input value={form.coSubs || ''} onChange={sf('coSubs')} placeholder="e.g. General Knowledge, Art Education, Yoga / P.T." /></Field></Span>
        </Grid>
        <ModalFooter><Btn onClick={() => setOpen(false)}>Cancel</Btn><Btn variant="primary" onClick={saveCls}>Save</Btn></ModalFooter>
      </Modal>
    </div>
  );
}


export default Classes;
